/**
 * VITAE-PUBLICA™ — Client-side vitalist calculator (mirrors backend engine).
 * 100% pure functions, zero LLM, no side effects.
 *
 * All formulas derive from the canonical equation:
 *     Jours_de_vie = Total_prélèvements / (Salaire_mensuel / Work_days)
 *
 * Backend reference: /app/backend/vitalist_engine.py :: compute_individual()
 */

/** Safe division — returns null if denominator is 0 or inputs are invalid. */
export function safeDiv(num, den) {
  if (num == null || den == null) return null;
  const n = Number(num);
  const d = Number(den);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
  return n / d;
}

/** Rounds to N digits, preserves null. */
export function round(x, digits = 4) {
  if (x == null || !Number.isFinite(Number(x))) return null;
  const p = 10 ** digits;
  return Math.round(Number(x) * p) / p;
}

/**
 * Compute per-profile vitalist indicators from inputs.
 * EXACT port of the backend compute_individual() formulas.
 *
 * @returns {{
 *   salaire_journalier, total_prelevements,
 *   jours_vie_sacrifies_par_an, mois_vie_par_an,
 *   annees_vie_sur_carriere, ratio_vie_fiscale_pct,
 *   ir_annuel, cotisations_annuelles, tva_annuelle
 * }}
 */
export function computeIndividual(inputs) {
  const monthly = Number(inputs.monthly_salary) || 0;
  const workDays = Number(inputs.work_days) || 30;
  const irRate = Number(inputs.ir_rate) || 0;
  const socialRate = Number(inputs.social_rate) || 0;
  const consumption = Number(inputs.consumption) || 0;
  const vatRate = Number(inputs.vat_rate) || 0;
  const otherTaxes = Number(inputs.other_taxes) || 0;
  const activeYears = Number(inputs.active_years) || 40;

  if (workDays <= 0) {
    return null;
  }
  const salaireJournalier = monthly / workDays;
  const irAnnuel = monthly * 12 * (irRate / 100);
  const cotisationsAnnuelles = monthly * 12 * (socialRate / 100);
  const tvaAnnuelle = consumption * 12 * (vatRate / 100);
  const totalPrelevements =
    irAnnuel + cotisationsAnnuelles + tvaAnnuelle + otherTaxes;
  const joursVie =
    safeDiv(totalPrelevements, salaireJournalier) || 0;
  const moisVie = joursVie / 30;
  const anneesCarriere = (joursVie * activeYears) / 365;
  const ratioPct = (joursVie / 365) * 100;

  return {
    salaire_journalier: round(salaireJournalier, 2),
    ir_annuel: round(irAnnuel, 2),
    cotisations_annuelles: round(cotisationsAnnuelles, 2),
    tva_annuelle: round(tvaAnnuelle, 2),
    total_prelevements: round(totalPrelevements, 2),
    jours_vie_sacrifies_par_an: round(joursVie, 4),
    mois_vie_par_an: round(moisVie, 4),
    annees_vie_sur_carriere: round(anneesCarriere, 4),
    ratio_vie_fiscale_pct: round(ratioPct, 3),
  };
}

/**
 * Compute delta metrics between a baseline individual and a simulated one.
 * Positive "recovered" days mean the simulated scenario FREES life from the state.
 */
export function computeDelta(baseline, simulated) {
  if (!baseline || !simulated) return null;
  const diffDays =
    (baseline.jours_vie_sacrifies_par_an || 0) -
    (simulated.jours_vie_sacrifies_par_an || 0);
  const diffMoney =
    (baseline.total_prelevements || 0) - (simulated.total_prelevements || 0);
  const diffYears =
    (baseline.annees_vie_sur_carriere || 0) -
    (simulated.annees_vie_sur_carriere || 0);
  return {
    days_recovered_per_year: round(diffDays, 3),
    money_recovered_per_year: round(diffMoney, 2),
    years_recovered_over_career: round(diffYears, 3),
  };
}

/**
 * Corruption recovery simulation:
 * If `pct` (0..100) of the diverted budget were restituted to citizens,
 * how many days of life per contributor does this represent?
 *
 * @param divertedBudgetUM    Estimated diverted budget (UM/year)
 * @param populationActive    Number of active taxpayers
 * @param salaireJournalier   Daily wage reference
 * @param pct                 Share recovered (0..100)
 */
export function simulateCorruptionRecovery({
  divertedBudgetUM,
  populationActive,
  salaireJournalier,
  pct,
}) {
  if (!divertedBudgetUM || !populationActive || !salaireJournalier) return null;
  const recoveredUM = (divertedBudgetUM * Math.max(0, Math.min(100, pct))) / 100;
  const perCitizen = safeDiv(recoveredUM, populationActive);
  const daysPerCitizen = safeDiv(perCitizen, salaireJournalier);
  return {
    recovered_total_um: round(recoveredUM, 2),
    recovered_per_citizen_um: round(perCitizen, 2),
    days_recovered_per_citizen: round(daysPerCitizen, 3),
  };
}

// ============================================================================
// Intergenerational debt projection
// ============================================================================

/**
 * Projects the mortgaged life-days for a child born in `birthYear`.
 *
 * Inputs:
 *   publicDebt            current total public debt (UM)
 *   populationTotal       total population (active + inactive + children)
 *   salaireJournalierMoyen reference daily wage
 *   debtGrowthRateAnnual  expected annual growth of debt (fraction, e.g. 0.05)
 *   horizons              array of years ahead to project (e.g. [10, 20, 30])
 *
 * Output:
 *   {
 *     perPerson: { debt_um, days_of_life, years_mortgaged },
 *     projection: [{ year_ahead, debt_um, days_of_life, years_mortgaged }, ...]
 *   }
 */
export function projectIntergenerationalDebt({
  publicDebt,
  populationTotal,
  salaireJournalierMoyen,
  debtGrowthRateAnnual = 0.05,
  horizons = [10, 20, 30],
}) {
  if (!publicDebt || !populationTotal || !salaireJournalierMoyen) return null;
  const perPersonUm = publicDebt / populationTotal;
  const days = safeDiv(perPersonUm, salaireJournalierMoyen);
  const years = safeDiv(days, 365);
  const projection = horizons.map((h) => {
    const projectedDebt = publicDebt * (1 + debtGrowthRateAnnual) ** h;
    const perPersonProj = projectedDebt / populationTotal;
    const daysProj = safeDiv(perPersonProj, salaireJournalierMoyen);
    return {
      year_ahead: h,
      debt_um: round(projectedDebt, 2),
      days_of_life: round(daysProj, 2),
      years_mortgaged: round(safeDiv(daysProj, 365), 3),
    };
  });
  return {
    perPerson: {
      debt_um: round(perPersonUm, 2),
      days_of_life: round(days, 2),
      years_mortgaged: round(years, 3),
    },
    projection,
  };
}

/**
 * Time series of mortgaged life-days over a child's active years (default 40).
 * Useful for a line chart showing cumulative burden.
 */
export function mortgageOverActiveYears({
  publicDebt,
  populationTotal,
  salaireJournalierMoyen,
  debtGrowthRateAnnual = 0.05,
  activeYears = 40,
}) {
  if (!publicDebt || !populationTotal || !salaireJournalierMoyen) return [];
  const points = [];
  for (let k = 0; k <= activeYears; k += 1) {
    const debtK = publicDebt * (1 + debtGrowthRateAnnual) ** k;
    const perPerson = debtK / populationTotal;
    const days = perPerson / salaireJournalierMoyen;
    points.push({
      year_ahead: k,
      days_of_life: round(days, 2),
      years_mortgaged: round(days / 365, 3),
    });
  }
  return points;
}

// ============================================================================
// Public sector dashboard (5 sectors × 4 columns)
// ============================================================================

/**
 * Default vitalist restitution benchmarks per sector (0..1+).
 * These are TRANSPARENT HEURISTICS — users can override each one via the UI.
 * Rationale:
 *  - Santé + Éducation typically > 1 when well-managed (positive externalities)
 *  - Infrastructure ~1 when maintained, <1 otherwise
 *  - Sécurité + Justice usually < 1 (consumption of time/life w/o direct gain)
 */
export const SECTOR_RESTITUTION_DEFAULTS = {
  Santé: 1.1,
  Éducation: 1.25,
  Sécurité: 0.55,
  Justice: 0.7,
  Infrastructure: 0.95,
};

export const SECTOR_ORDER = [
  "Santé",
  "Éducation",
  "Sécurité",
  "Justice",
  "Infrastructure",
];

const SECTOR_KEYWORDS = {
  Santé: ["sant", "health", "hôp", "medic", "soin"],
  Éducation: ["éduc", "educ", "écol", "enseign", "univ", "school"],
  Sécurité: ["défense", "defense", "armée", "police", "intérieur", "sécur", "milit"],
  Justice: ["justic", "tribunal", "pénit", "penit", "magistrat"],
  Infrastructure: [
    "infrast",
    "travaux",
    "transport",
    "route",
    "énergie",
    "energ",
    "eau ",
    "numéri",
    "digital",
  ],
};

/**
 * Aggregates a list of budget lines into the 5 canonical sectors.
 */
export function aggregateBudgetIntoSectors(aggregatedBudgetLines = []) {
  const totals = Object.fromEntries(SECTOR_ORDER.map((s) => [s, 0]));
  aggregatedBudgetLines.forEach((bl) => {
    const raw = (bl.sector || "").toLowerCase();
    for (const [sector, kws] of Object.entries(SECTOR_KEYWORDS)) {
      if (kws.some((k) => raw.includes(k))) {
        totals[sector] += Number(bl.amount) || 0;
        break;
      }
    }
  });
  return totals;
}

/**
 * Builds the 5-row public sector dashboard table.
 * Each row: sector, daysInvested, daysRestituted, ratio, severity.
 */
export function buildPublicSectorDashboard({
  aggregatedBudgetLines = [],
  salaireJournalierMoyen,
  populationActive,
  restitutionOverrides = {},
}) {
  const sectorTotals = aggregateBudgetIntoSectors(aggregatedBudgetLines);
  const rows = SECTOR_ORDER.map((sector) => {
    const totalUm = sectorTotals[sector] || 0;
    const perCapita =
      populationActive > 0 ? totalUm / populationActive : totalUm;
    const daysInvested =
      salaireJournalierMoyen > 0
        ? round(perCapita / salaireJournalierMoyen, 2)
        : null;
    const ratio =
      restitutionOverrides[sector] != null
        ? Number(restitutionOverrides[sector])
        : SECTOR_RESTITUTION_DEFAULTS[sector] || 1;
    const daysRestituted =
      daysInvested != null ? round(daysInvested * ratio, 2) : null;
    const pct = round(ratio * 100, 1);
    let severity = "red";
    if (pct >= 100) severity = "green";
    else if (pct >= 50) severity = "orange";
    return {
      sector,
      total_um: round(totalUm, 2),
      days_invested: daysInvested,
      days_restituted: daysRestituted,
      ratio,
      ratio_pct: pct,
      severity,
    };
  });
  return rows;
}
